import React from 'react';
import { WebsiteNavbar } from './WebsiteNavbar';
import { WebsiteFooter } from './WebsiteFooter';
import { 
  Leaf, 
  TrendingUp, 
  FileText, 
  Users,
  CheckCircle,
  ArrowRight,
  ThermometerSun,
  Zap,
  Home,
  Euro
} from 'lucide-react';
import { Link } from 'react-router-dom';

const WebsiteRenovation = () => {
  const services = [
    {
      icon: FileText,
      title: 'Audit Énergétique',
      description: 'Diagnostic complet de la performance énergétique de votre copropriété.'
    },
    {
      icon: TrendingUp,
      title: 'Plan de Travaux',
      description: 'Élaboration d\'un plan pluriannuel de travaux adapté à votre budget.'
    },
    {
      icon: Euro,
      title: 'Recherche de Financements',
      description: 'Identification des aides et subventions disponibles (MaPrimeRénov\', CEE, etc.).'
    },
    {
      icon: Users,
      title: 'Accompagnement AG',
      description: 'Présentation du projet et accompagnement au vote en assemblée générale.'
    }
  ];

  const benefits = [
    'Réduction jusqu\'à 40% de vos charges énergétiques',
    'Valorisation de votre patrimoine immobilier',
    'Amélioration du confort thermique été/hiver',
    'Contribution à la transition écologique',
    'Conformité aux obligations réglementaires',
    'Accès aux aides de l\'État'
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="website-renovation">
      <WebsiteNavbar />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur rounded-full text-sm mb-6">
                <Leaf className="w-4 h-4" />
                <span>Transition énergétique</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                La rénovation énergétique de votre copropriété
              </h1>
              <p className="text-xl text-white/80 mb-8">
                Accompagnement complet pour réduire vos charges et valoriser votre patrimoine, 
                en conformité avec les obligations légales.
              </p>
              <Link
                to="/site/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-emerald-600 rounded-full font-semibold hover:shadow-xl transition-all"
              >
                Demander un diagnostic gratuit
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600"
                alt="Rénovation énergétique"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                    <ThermometerSun className="w-7 h-7 text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-slate-900">-40%</div>
                    <div className="text-slate-500 text-sm">sur vos charges</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Notre accompagnement
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Un service clé en main pour mener à bien votre projet de rénovation énergétique.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-lg transition-shadow"
              >
                <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Les avantages de la rénovation énergétique
              </h2>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-4">
                    <CheckCircle className="w-6 h-6 text-emerald-500 flex-shrink-0" />
                    <span className="text-lg text-slate-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-8 text-white">
                <Zap className="w-10 h-10 mb-4" />
                <div className="text-3xl font-bold mb-2">DPE</div>
                <p className="text-white/80">Amélioration de votre classement énergétique</p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-8 text-white mt-8">
                <Home className="w-10 h-10 mb-4" />
                <div className="text-3xl font-bold mb-2">+15%</div>
                <p className="text-white/80">Valeur de votre bien immobilier</p>
              </div>
              <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-8 text-white">
                <Euro className="w-10 h-10 mb-4" />
                <div className="text-3xl font-bold mb-2">Aides</div>
                <p className="text-white/80">MaPrimeRénov', CEE, subventions locales</p>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl p-8 text-white mt-8">
                <Leaf className="w-10 h-10 mb-4" />
                <div className="text-3xl font-bold mb-2">CO2</div>
                <p className="text-white/80">Réduction de votre empreinte carbone</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Démarrez votre projet de rénovation
          </h2>
          <p className="text-xl text-slate-400 mb-8">
            Contactez-nous pour un diagnostic gratuit et sans engagement
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+33755608550"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-emerald-500 text-white rounded-full font-semibold hover:bg-emerald-600 transition-all"
            >
              Appeler le 07 55 60 85 50
            </a>
            <a
              href="mailto:renovation@syndicappli.fr"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white rounded-full font-semibold hover:bg-white/20 transition-all"
            >
              renovation@syndicappli.fr
            </a>
          </div>
        </div>
      </section>

      <WebsiteFooter />
    </div>
  );
};

export { WebsiteRenovation };
